// import { analyticsService } from './analyticsService';

/**
 * Utility function to fetch with timeout
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Response>}
 */
const fetchWithTimeout = async (url, options = {}, timeout = 10000, abortController = null) => {
    // Use provided abort controller or create a new one
    const controller = abortController || new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
        const response = await fetch(url, { 
            ...options, 
            signal: controller.signal 
        });
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error('Request cancelled');
        }
        throw new Error('Unable to connect to the website. Please check the URL and try again.');
    }
};

/**
 * Validates URL format
 * @param {string} url - URL to validate
 * @returns {boolean}
 */
const isValidUrlFormat = (url) => {
    const urlPattern = /^(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9][a-zA-Z0-9-]*\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?$/;
    return urlPattern.test(url);
};

/**
 * Checks a specific URL for Shopify markers
 * @param {string} urlToCheck - URL to check
 * @returns {Promise<{isShopify: boolean, workingUrl?: string}>}
 */
const checkUrlForShopify = async (urlToCheck) => {
    try {
        // Only check products.json endpoint
        const response = await fetchWithTimeout(`${urlToCheck}/products.json`);
        if (response.ok) {
            const data = await response.json();
            if (data.products) {
                return { isShopify: true, workingUrl: urlToCheck };
            }
        }
        return { isShopify: false };
    } catch {
        return { isShopify: false };
    }
};

/**
 * Validates if the URL is a Shopify store
 * @param {string} url - URL to validate
 * @returns {Promise<{isShopify: boolean, workingUrl?: string}>}
 */
const validateShopifyStore = async (url) => {
    try {
        // First try the main domain
        const mainResult = await checkUrlForShopify(url);
        if (mainResult.isShopify) {
            return mainResult;
        }

        // If main domain check fails, try common Shopify-related subdomains
        try {
            const urlObj = new URL(url);
            // Reduced list of common subdomains
            const commonSubdomains = [
                'checkout',
                'shop',
                'store',
                'buy'
            ];

            for (const subdomain of commonSubdomains) {
                const subdomainUrl = `https://${subdomain}.${urlObj.hostname}`;
                const result = await checkUrlForShopify(subdomainUrl);
                if (result.isShopify) {
                    return result;
                }
            }

            // Also try the www subdomain if not already tried
            if (!urlObj.hostname.startsWith('www.')) {
                const wwwUrl = `https://www.${urlObj.hostname}`;
                const result = await checkUrlForShopify(wwwUrl);
                if (result.isShopify) {
                    return result;
                }
            }
        } catch {
            // Ignore subdomain check errors
        }

        return { isShopify: false };
    } catch (error) {
        return { isShopify: false };
    }
};

/**
 * Gets products with free variants from a Shopify store
 * @param {string} url - Store URL
 * @returns {Promise<Array>} Array of free products
 */
const getProducts = async (url, isStoppedRef = null) => {
    const controller = new AbortController();
    const freeProducts = [];
    const allProducts = [];
    
    try {
        let page = 1;
        let hasMoreProducts = true;
        const limit = 250;
        const MAX_PAGES = 20;
        const CONCURRENT_PAGES = 10;

        if (isStoppedRef) {
            const checkStopInterval = setInterval(() => {
                if (isStoppedRef.current) {
                    controller.abort();
                    clearInterval(checkStopInterval);
                }
            }, 100);
        }

        while (hasMoreProducts && page <= MAX_PAGES) {
            if (isStoppedRef?.current) {
                break;
            }

            const pagePromises = [];
            for (let i = 0; i < CONCURRENT_PAGES && hasMoreProducts && (page + i) <= MAX_PAGES; i++) {
                const currentPage = page + i;
                pagePromises.push(
                    fetchWithTimeout(
                        `${url}/products.json?limit=${limit}&page=${currentPage}`,
                        {},
                        30000,
                        controller
                    ).then(response => {
                        if (!response.ok) {
                            throw new Error('Failed to fetch products');
                        }
                        return response.json();
                    })
                );
            }

            try {
                const results = await Promise.all(pagePromises);
                
                for (const data of results) {
                    if (isStoppedRef?.current) {
                        return { freeProducts, allProducts };
                    }

                    if (!data.products || data.products.length === 0) {
                        hasMoreProducts = false;
                        break;
                    }

                    for (const product of data.products) {
                        const mapVariant = (variant) => ({
                            id: variant.id,
                            title: variant.title,
                            price: variant.price,
                            sku: variant.sku,
                            available: variant.available
                        });

                        allProducts.push({
                            id: product.id,
                            title: product.title,
                            handle: product.handle,
                            productUrl: `${url}/products/${product.handle}`,
                            featuredImage: product.images[0]?.src || null,
                            freeVariants: product.variants.map(mapVariant)
                        });

                        const hasFreeVariant = product.variants.some(variant => 
                            parseFloat(variant.price) === 0.00
                        );

                        if (hasFreeVariant) {
                            freeProducts.push({
                                id: product.id,
                                title: product.title,
                                handle: product.handle,
                                productUrl: `${url}/products/${product.handle}`,
                                featuredImage: product.images[0]?.src || null,
                                freeVariants: product.variants
                                    .filter(variant => parseFloat(variant.price) === 0.00)
                                    .map(mapVariant)
                            });
                        }
                    }

                    hasMoreProducts = data.products.length === limit;
                }
            } catch (error) {
                if (error.message === 'Request cancelled') {
                    return { freeProducts, allProducts };
                }
                throw error;
            }

            page += CONCURRENT_PAGES;

            if (hasMoreProducts && !isStoppedRef?.current) {
                await new Promise(resolve => setTimeout(resolve, 50));
            }
        }

        return { freeProducts, allProducts };
    } catch (error) {
        if (error.message === 'Request cancelled') {
            return { freeProducts, allProducts };
        }
        if (error.message.includes('timed out')) {
            throw new Error('Connection timed out. Please check the URL and try again.');
        }
        throw new Error('Failed to process products. Please check the URL and try again.');
    }
};

class LinkService {
    normalizeUrl(url) {
        try {
            // Add https if no protocol specified
            if (!url.startsWith('http')) {
                url = 'https://' + url;
            }
            
            // Parse URL and extract hostname
            const urlObj = new URL(url);
            const domain = urlObj.hostname;
            
            // Remove www if present
            const baseDomain = domain.replace(/^www\./, '');
            
            return `https://${baseDomain}`;
        } catch (error) {
            console.error('URL normalization error:', error);
            return url;
        }
    }

    async processLink(url, isStoppedRef = null) {
        const startTime = Date.now();
        try {
            const normalizedUrl = this.normalizeUrl(url);
            let processedUrl = normalizedUrl.trim();
            
            if (!processedUrl) {
                throw new Error('Please enter a URL');
            }

            if (!isValidUrlFormat(processedUrl)) {
                throw new Error('Invalid URL format. Please enter a valid website address (e.g., example.com)');
            }

            // Add protocol if missing
            if (!processedUrl.match(/^[a-zA-Z]+:\/\//)) {
                try {
                    const httpsUrl = new URL(`https://${processedUrl}`);
                    processedUrl = httpsUrl.href;
                } catch {
                    try {
                        const httpUrl = new URL(`http://${processedUrl}`);
                        processedUrl = httpUrl.href;
                    } catch (error) {
                        throw new Error('Invalid URL format. Please enter a valid website address.');
                    }
                }
            }

            // Final URL validation
            try {
                const validUrl = new URL(processedUrl);
                processedUrl = validUrl.href;
            } catch (error) {
                throw new Error('Invalid URL format. Please enter a valid website address.');
            }
            
            const shopifyResult = await validateShopifyStore(processedUrl);

            if (!shopifyResult.isShopify) {
                throw new Error('This website is not a Shopify store. Please enter a valid Shopify store URL.');
            }

            const workingUrl = shopifyResult.workingUrl || processedUrl;
            const { freeProducts, allProducts } = await getProducts(workingUrl, isStoppedRef);
            
            const processingTime = Date.now() - startTime;

            return {
                success: true,
                originalUrl: url,
                processedUrl: processedUrl,
                protocol: new URL(processedUrl).protocol,
                wasProtocolAdded: url !== processedUrl,
                timestamp: new Date().toISOString(),
                isShopifyStore: true,
                freeProducts: freeProducts,
                allProducts: allProducts,
                totalFreeProducts: freeProducts.length,
                totalProducts: allProducts.length,
                processingTime,
                ...(freeProducts.length === 0 && { message: 'No free products found in this store.' })
            };
        } catch (error) {
            throw new Error(error.message || 'Failed to process the URL. Please try again.');
        }
    }
}

export const linkService = new LinkService(); 